import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  onAuthStateChanged, 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut 
} from 'firebase/auth';
import { doc, setDoc, getDocFromCache } from 'firebase/firestore';
import { auth, db } from '../firebase';

const AuthContext = createContext();

const formatAuthError = (error) => {
  const code = error?.code || error?.customData?._tokenResponse?.error?.message;

  if (code === 'auth/api-key-expired') {
    const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || '(unknown project)';
    return (
      'Firebase API key expired for this app. ' +
      `Renew/rotate the Firebase Web API key for project "${projectId}" and update ` +
      'REACT_APP_FIREBASE_API_KEY in your environment, then rebuild/redeploy.'
    );
  }

  return error?.message || 'Unknown authentication error';
};

const normalizeRole = (role) => {
  if (typeof role !== 'string') {
    return null;
  }

  return role.trim().toLowerCase();
};

const getAuthRole = async (user) => {
  if (!user) {
    return null;
  }

  try {
    const tokenResult = await user.getIdTokenResult(false);
    const tokenClaims = tokenResult?.claims || {};
    const tokenRole = normalizeRole(tokenClaims.role);

    if (tokenRole) {
      return tokenRole;
    }

    const cachedDoc = await getDocFromCache(doc(db, 'users', user.uid));
    if (cachedDoc.exists()) {
      return normalizeRole(cachedDoc.data()?.role);
    }
  } catch (error) {
    console.error('Error resolving auth role:', error);
  }

  return null;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);

  // Register new user with role
  const register = async (email, password, name, role = 'not-validated') => {
    try {
      const { user } = await createUserWithEmailAndPassword(auth, email, password);

      // Create user document in Firestore
      const userData = {
        name,
        email,
        role,
        createdAt: new Date().toISOString()
      };

      await setDoc(doc(db, 'users', user.uid), userData);

      // Important: Sign out the user immediately after registration since they're not validated
      await signOut(auth);

      return user;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  };

  // Login user
  const login = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      const role = await getAuthRole(user);

      return userCredential;
    } catch (error) {
      console.error('Login error:', error);
      throw new Error(formatAuthError(error));
    }
  };

  // Logout user
  const logout = () => {
    return signOut(auth);
  };

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const role = await getAuthRole(user);
        setCurrentUser(user);
        setUserRole(role);
      } else {
        setCurrentUser(null);
        setUserRole(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userRole,
    register,
    login,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
